import { Link } from "@inertiajs/react"
import { Building2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Empty } from "@/components/ui/empty"
import { formatCurrency } from "@/lib/utils"
import { partnerAccountPath, partnerAccountsPath } from "@/routes"

export interface TopAccount {
  uid: string
  name: string
  industry: string | null
  agent_count: number
  mrr_cents: number
}

export function TopAccountsTable({ accounts }: { accounts: TopAccount[] }) {
  return (
    <Card className="flex h-full flex-col rounded-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold">
            Top Accounts
          </CardTitle>
          <Button variant="outline" size="sm" className="rounded-sm" asChild>
            <Link href={partnerAccountsPath()}>View all</Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        {accounts.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <Empty
              icon={<Building2 className="h-5 w-5" />}
              title="No accounts yet"
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-foreground min-w-full text-left text-sm/6">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="border-b px-4 py-2 font-medium first:pl-1 last:pr-1">
                    Account
                  </th>
                  <th className="border-b px-4 py-2 text-right font-medium first:pl-1 last:pr-1">
                    MRR
                  </th>
                  <th className="border-b px-4 py-2 text-right font-medium first:pl-1 last:pr-1">
                    Agents
                  </th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.uid} className="hover:bg-muted/50">
                    <td className="border-b px-4 py-4 first:pl-1 last:pr-1">
                      <Link
                        href={partnerAccountPath({ id: account.uid })}
                        className="underline-offset-4 hover:underline"
                      >
                        <div className="font-medium">{account.name}</div>
                        {account.industry && (
                          <div className="text-muted-foreground text-sm">
                            {account.industry}
                          </div>
                        )}
                      </Link>
                    </td>
                    <td className="border-b px-4 py-4 text-right tabular-nums first:pl-1 last:pr-1">
                      {account.mrr_cents > 0
                        ? formatCurrency(account.mrr_cents / 100)
                        : "—"}
                    </td>
                    <td className="border-b px-4 py-4 text-right tabular-nums first:pl-1 last:pr-1">
                      {account.agent_count > 0 ? account.agent_count : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
